import { Component, Input, OnChanges, OnDestroy, OnInit, output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatFormFieldModule, SubscriptSizing } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule } from '@ngneat/transloco';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'timeconverter-component',
    templateUrl: './timeconverter.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule, TranslocoModule]
})
export class TimeConverterComponent implements OnDestroy, OnChanges {

    @Input() control: FormControl<number>;
    @Input() label: string = '';
    @Input() labelClass: string = '';
    @Input() hint: string = '';
    @Input() format: string = 'DAYS';
    @Input() isUpdate = false;
    @Input() useSummaryView = false;
    @Input() readonly = false;
    @Input() required = false;
    @Input() hidden = false;
    @Input() nullable = false;
    @Input() subscriptSizing: SubscriptSizing = 'fixed';
    @Input() hideHints = false;
    @Input() markAsTouched: Subject<void>;
    onBlur = output<void>();
    
    valueChange = output<void>();

    controlOriginal: FormControl;
    defaultValue: string;

    private readonly timeRegexp = /(^[0-9]+(d|D)((\s+[0-9]+(h|H))?(\s+[0-9]+(m|M)((\s+)?$)|(\s+)?$)|(\s+)?$))|(^[0-9]+(h|H)(\s+[0-9]+(m|M)((\s+)?$)|(\s+)?$))|(^[0-9]+(m|M)((\s+)?$))/;

    private _unsubscribeAll = new Subject<void>();

    constructor() { }

    ngOnChanges(changes: SimpleChanges) {
        if ('control' in changes) {
            this.initComponent();
        }
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    private initComponent() {
        const originalValue: string = this.control.value !== null && this.control.value !== undefined ?
            this.minutesToString(this.control.value) : '';

        const validators: ValidatorFn[] = [
            (control: AbstractControl) => {
                if (this.required) {
                    if (this.control.invalid) {
                        return { required: true };
                    }
                } else {
                    return null;
                }
            },
            Validators.pattern(this.timeRegexp)
        ];

        this.controlOriginal = new FormControl(originalValue, validators);
        if (this.control.disabled || this.readonly) {
            this.controlOriginal.disable();
        } else {
            this.control.addValidators((control: AbstractControl) => {
                if (this.controlOriginal.value && control.value === undefined) {
                    return { invalidTime: true }
                } else {
                    return null;
                }
            });
        }
        this.defaultValue = this.controlOriginal.value;

        this.controlOriginal.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((value) => {
                this.valueChange.emit();
                if (value === '') {
                    this.control.setValue(undefined);
                } else {
                    let totalMinutes = 0;
                    const timeData = value.toLowerCase().split(' ');
                    if (!this.controlOriginal.errors?.pattern) {
                        for (const t of timeData) {
                            if (t.includes('d')) {
                                const days = Number(t.split('d')[0]);
                                totalMinutes += days * 24 * 60;
                            } else if (t.includes('h')) {
                                const hours = Number(t.split('h')[0]);
                                totalMinutes += hours * 60;
                            } else if (t.includes('m')) {
                                const minutes = Number(t.split('m')[0]);
                                totalMinutes += minutes;
                            }
                        }
                    } else {
                        totalMinutes = undefined;
                    }
                    this.control.setValue(totalMinutes);
                }
                this.control.markAsDirty();
            });

        this.control.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                distinctUntilChanged()
            )
            .subscribe((value) => {
                if (value !== undefined || this.timeRegexp.test(this.controlOriginal.value)) {
                    this.controlOriginal.setValue(this.minutesToString(value), { emitEvent: false });
                }
            });
        
        this.control.registerOnDisabledChange(disabled => {
            if (disabled) {
                this.controlOriginal.disable();
            } else {
                this.controlOriginal.enable();
                this.control.addValidators((control: AbstractControl) => {
                    if (this.controlOriginal.value && control.value === undefined) {
                        return { invalidTime: true }
                    } else {
                        return null;
                    }
                });
            }
        });

        if (this.markAsTouched) {
            this.markAsTouched.pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => this.controlOriginal.markAllAsTouched());
        }
    }

    private minutesToString(minutes: number) {
        minutes = minutes ?? 0;
        const days = this.format === 'DAYS' ? Math.floor(minutes / 1440) : 0;
        let hours = 0;
        if (this.format === 'DAYS') {
            hours = Math.floor((minutes % 1440) / 60);
        } else if (this.format === 'HOURS') {
            hours = Math.floor(minutes / 60);
        }
        let str = '';
        minutes = this.format === 'MINUTES' ? minutes : minutes % 60;
        if (days > 0) {
            str = str + String(days) + 'd ';
        }
        if (hours > 0) {
            str = str + String(hours) + 'h ';
        }
        if (minutes > 0) {
            str = str + String(Math.round(minutes)) + 'm';
        }
        if (str === '') {
            return this.nullable ? '' : '0m';
        } else {
            return str;
        }
    }

}
